import { GridApi } from 'ag-grid-enterprise';
import {
  APIComponentAttributes,
  APIScopeData,
  ArdoqId,
} from '@ardoq/api-types';
import {
  InventoryAccessControl,
  InventoryContext,
  SelectedComponent,
} from './types';
import { refreshServerSideForGrid } from './serverSideRefresh';
import {
  enhancedScopeDataOperations,
  scopeDataOperations,
} from '@ardoq/scope-data';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  ensureContrast,
  getLightenedColor,
  getShadedColor,
} from '@ardoq/color-helpers';
import { isTruthy } from '@ardoq/common-helpers';
import { dateRangeOperations } from '@ardoq/date-range';
import { reportComponentRowsOperations } from './reportComponentRowsOperations';

const toSelectedComponent = (
  {
    name,
    _id,
    entityType,
    rootWorkspace,
    model,
    typeId,
    lock,
  }: APIComponentAttributes,
  scopeData: EnhancedScopeData
): SelectedComponent => {
  const color = enhancedScopeDataOperations.getComponentColor(scopeData, _id);
  const lightenedColor = getLightenedColor(color);
  const shadedColor = getShadedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);

  return {
    name,
    _id,
    rootWorkspace,
    model,
    typeId,
    lock,
    typeName: entityType,
    representationData: {
      ...enhancedScopeDataOperations.getComponentRepresentationData(
        scopeData,
        _id
      ),
      color,
      shadedColor,
      contrastColor,
      lightenedColor,
    },
  };
};

const getSelectedComponents = (
  api: GridApi<APIComponentAttributes>,
  scopeData: EnhancedScopeData
): SelectedComponent[] =>
  api
    .getSelectedRows()
    // when user selects the first component and scrolls to the
    // bottom of the table of a large workspace/component type, some pages
    // are not loaded, so the components array can contain undefined values.
    // This filter ensures that we at least don't crush, but we need a special
    // UI for large datasets
    .filter(isTruthy)
    .map(component => toSelectedComponent(component, scopeData));

const updateAccessControlContext = (
  api: GridApi,
  accessControl: InventoryAccessControl
): void => {
  const context = api.getGridOption('context') as InventoryContext | null;
  if (!context) return;

  api.setGridOption('context', { ...context, accessControl });
};

const patchComponentsIntoScopeData = (
  gridApi: GridApi<APIComponentAttributes>,
  components: APIComponentAttributes[]
): void => {
  const context = gridApi.getGridOption('context') as InventoryContext | null;
  if (!context) return;

  const updatesById = new Map(
    components.map(component => [component._id, component])
  );
  const apiScopeData: APIScopeData = {
    ...context.apiScopeData,
    components: context.apiScopeData.components.map(component => {
      const update = updatesById.get(component._id);
      return update ? { ...component, ...update } : component;
    }),
  };

  gridApi.setGridOption('context', {
    ...context,
    apiScopeData,
    scopeData: scopeDataOperations.enhance(apiScopeData),
  });
};

/**
 * Applies updated components to the grid: patches them into the scope data in
 * the grid context and updates their row data.
 */
const applyComponentUpdatesToRows = (
  gridApi: GridApi<APIComponentAttributes>,
  components: APIComponentAttributes[]
): void => {
  const context = gridApi.getGridOption('context') as InventoryContext | null;
  if (!context) return;

  const updatedComponentsInScopeData = components
    .filter(({ _id }) => context.scopeData.componentsById[_id])
    .map(component =>
      dateRangeOperations.mergeDateRangeFieldsForEntity(
        component,
        context.scopeData.dateRangeFieldMap
      )
    );
  if (updatedComponentsInScopeData.length === 0) return;

  patchComponentsIntoScopeData(gridApi, updatedComponentsInScopeData);
  const { scopeData } = gridApi.getGridOption('context') as InventoryContext;

  updatedComponentsInScopeData.forEach(component => {
    const rowNode = gridApi.getRowNode(component._id);
    if (!rowNode) return;

    rowNode.updateData(
      reportComponentRowsOperations.mapComponentFromApiComponentFormat(
        component,
        scopeData,
        rowNode.data
      )
    );
    gridApi.refreshCells({ rowNodes: [rowNode], force: true });
  });

  gridApi.refreshCells({ force: true, columns: ['parent'] });
};

/**
 * It's possible for a row to be pinned and in the main table (when you create new row and then change filters or sorting).
 * We make sure that we remove both such rows if that happens.
 */
const removeComponentsFromPinnedRows = (
  gridApi: GridApi<APIComponentAttributes>,
  deletedComponentIds: ArdoqId[]
) => {
  gridApi.setGridOption('pinnedTopRowData', [
    ...(gridApi.getGridOption('pinnedTopRowData') ?? []).filter(
      (component: APIComponentAttributes) =>
        !deletedComponentIds.includes(component._id)
    ),
  ]);

  const deletedComponentsFromMainTable = deletedComponentIds
    .map(it => gridApi.getRowNode(it))
    .filter(Boolean);
  if (deletedComponentsFromMainTable.length > 0) {
    refreshServerSideForGrid(gridApi);
  }
};

export const inventoryGridAPIOperations = {
  getSelectedComponents,
  toSelectedComponent,
  updateAccessControlContext,
  applyComponentUpdatesToRows,
  removeComponentsFromPinnedRows,
};
